import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { scenariosAPI } from '@/services/api';
import { MessageCircle, ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Tópicos para conversa livre
const TOPICS = [
  { id: 'travel', label: 'Viagens', emoji: '✈️', color: 'bg-blue-50 border-blue-200 hover:bg-blue-100' },
  { id: 'work', label: 'Trabalho', emoji: '💼', color: 'bg-purple-50 border-purple-200 hover:bg-purple-100' },
  { id: 'movies', label: 'Filmes', emoji: '🎬', color: 'bg-pink-50 border-pink-200 hover:bg-pink-100' },
  { id: 'food', label: 'Comida', emoji: '🍕', color: 'bg-orange-50 border-orange-200 hover:bg-orange-100' },
  { id: 'hobbies', label: 'Hobbies', emoji: '🎨', color: 'bg-green-50 border-green-200 hover:bg-green-100' },
  { id: 'family', label: 'Família', emoji: '👨‍👩‍👧', color: 'bg-red-50 border-red-200 hover:bg-red-100' },
];

// Scenario images
const scenarioImages = {
  restaurant: 'https://images.pexels.com/photos/29489315/pexels-photo-29489315.jpeg?auto=compress&cs=tinysrgb&w=300',
  job_interview: 'https://images.unsplash.com/photo-1637665662134-db459c1bbb46?w=300&h=200&fit=crop',
  airport: 'https://images.unsplash.com/photo-1773734874748-8e7893de8e6f?w=300&h=200&fit=crop',
  shopping: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=200&fit=crop',
  doctor: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=300&h=200&fit=crop',
  hotel: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop',
};

const ChatPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [activeTab, setActiveTab] = useState('topics'); // 'topics' or 'scenarios'
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const data = await scenariosAPI.getAll();
      setScenarios(data.slice(0, 6)); // Show first 6 scenarios
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    } finally {
      setLoadingScenarios(false);
    }
  };

  const startConversation = async (topic) => {
    setLoading(true);
    setSelectedTopic(topic.id);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/chat/conversation?mode=free_chat&scenario=${topic.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const data = await response.json();
      
      if (data.id) {
        navigate(`/chat/${data.id}`, { 
          state: { topic: topic.id, topicLabel: topic.label }
        });
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erro ao iniciar conversa');
      setLoading(false);
    }
  };

  const startScenarioLesson = (scenarioId) => {
    navigate(`/guided-lesson/${scenarioId}`);
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white border-b border-forest/10 px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime/30 rounded-full flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-forest" />
          </div>
          <div>
            <h1 className="font-fraunces text-xl font-semibold text-forest">Conversar</h1>
            <p className="text-xs text-stone">Escolha como quer praticar</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 pb-24">
        {/* Lira Welcome */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 shadow-soft mb-5"
        >
          <div className="flex items-center gap-3">
            <img 
              src="https://images.unsplash.com/photo-1697063882499-f7fca7d2d713?w=60&h=60&fit=crop&crop=face"
              alt="Lira"
              className="w-11 h-11 rounded-full object-cover border-2 border-lime"
            />
            <div>
              <span className="font-medium text-forest block text-sm">Lira</span>
              <p className="text-stone text-xs">
                Vamos praticar {user?.target_language || 'inglês'} juntos! 💚
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tab Switcher */}
        <div className="flex bg-forest/5 rounded-xl p-1 mb-5">
          <button
            onClick={() => setActiveTab('topics')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'topics' 
                ? 'bg-white text-forest shadow-sm' 
                : 'text-stone hover:text-forest'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Conversa Livre
          </button>
          <button
            onClick={() => setActiveTab('scenarios')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'scenarios' 
                ? 'bg-white text-forest shadow-sm' 
                : 'text-stone hover:text-forest'
            }`}
          >
            <BookOpen className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Lições Guiadas
          </button>
        </div>

        {/* Topics Tab */}
        <AnimatePresence mode="wait">
          {activeTab === 'topics' && (
            <motion.div
              key="topics"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <h2 className="text-sm font-medium text-stone mb-3">
                Sobre o que você quer conversar?
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {TOPICS.map((topic, index) => (
                  <motion.button
                    key={topic.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => startConversation(topic)}
                    disabled={loading}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${topic.color} ${
                      selectedTopic === topic.id 
                        ? 'ring-2 ring-forest ring-offset-2' 
                        : 'active:scale-[0.98]'
                    } ${loading ? 'opacity-50' : ''}`}
                    data-testid={`topic-${topic.id}`}
                  >
                    <span className="text-2xl mb-2 block">{topic.emoji}</span>
                    <span className="font-medium text-forest text-sm">{topic.label}</span>
                    
                    {loading && selectedTopic === topic.id && (
                      <div className="mt-2">
                        <div className="w-4 h-4 border-2 border-forest/20 border-t-forest rounded-full animate-spin" />
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              {/* History Link */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-5"
              >
                <Button
                  variant="ghost"
                  onClick={() => navigate('/conversations')}
                  className="w-full text-stone hover:text-forest"
                  data-testid="view-history-btn"
                >
                  Ver histórico de conversas
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Scenarios Tab */}
          {activeTab === 'scenarios' && (
            <motion.div
              key="scenarios"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-sm font-medium text-stone mb-3">
                Aprenda frases úteis para situações reais
              </h2>

              {loadingScenarios ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-forest/20 border-t-forest rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {scenarios.map((scenario, index) => (
                    <motion.button
                      key={scenario.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => startScenarioLesson(scenario.id)}
                      className="w-full bg-white rounded-xl p-3 flex items-center gap-3 shadow-soft active:scale-[0.98] transition-transform text-left"
                      data-testid={`scenario-${scenario.id}`}
                    >
                      <img
                        src={scenarioImages[scenario.id] || scenarioImages.restaurant}
                        alt={scenario.title}
                        className="w-16 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-forest text-sm truncate">
                          {scenario.title}
                        </h3>
                        <p className="text-xs text-stone truncate">
                          {scenario.description}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-stone/40 flex-shrink-0" />
                    </motion.button>
                  ))}
                </div>
              )}

              {/* See All Scenarios */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-5"
              >
                <Button
                  variant="ghost"
                  onClick={() => navigate('/scenarios')}
                  className="w-full text-stone hover:text-forest"
                >
                  Ver todos os cenários
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ChatPage;
